import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function BuySellAgreementFunding() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="buy-sell-agreement-funding" />
          {/* Header */}
          <div className="text-center mb-12">
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Buy-Sell Agreement Funding</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Ensuring Smooth Business Transitions Through Strategic Funding
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is a Buy-Sell Agreement & Funding? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is Buy-Sell Agreement Funding?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                A Buy-Sell Agreement is a legally binding contract between co-owners of a business that governs the situation if a co-owner dies, is forced to leave the business, or chooses to leave. It stipulates how the departing owner's share will be bought out by the remaining owners or the company itself.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                <strong>Buy-Sell Agreement Funding</strong> refers to the mechanism used to ensure sufficient funds are available to execute the buyout terms outlined in the agreement. Without adequate funding, the agreement may be impossible to fulfill, leading to disputes, forced sales, or instability. Life insurance is a common and effective funding method.
              </Text>
            </Card>

            {/* How It Works (Using Life Insurance) */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How Funding Works (Commonly with Life Insurance)</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Establish Agreement:</strong> Business owners create a comprehensive Buy-Sell Agreement defining trigger events (death, disability, retirement, etc.), valuation methods, and buyout terms.
                </li>
                <li>
                  <strong>Determine Funding Need:</strong> Calculate the value of each owner's share based on the agreement's valuation formula. This determines the amount of funding required.
                </li>
                <li>
                  <strong>Purchase Insurance Policies:</strong> Depending on the agreement structure (Cross-Purchase or Entity-Purchase/Stock Redemption), insurance policies (typically life and possibly disability insurance) are purchased:
                    •   <strong>Cross-Purchase:</strong> Each owner buys a policy on each of the <em>other</em> owners.
                    •   <strong>Entity-Purchase:</strong> The business entity buys separate policies on <em>each</em> owner.
                </li>
                <li>
                  <strong>Pay Premiums:</strong> The policy owners (either the individuals or the business) pay the premiums.
                </li>
                <li>
                  <strong>Trigger Event Occurs:</strong> If an insured event happens (e.g., an owner passes away), the policy pays out the death benefit.
                </li>
                <li>
                  <strong>Execute Buyout:</strong> The policy proceeds are used by the designated buyers (remaining owners or the company) to purchase the departing owner's share from their estate or the owner directly (in case of disability/retirement triggers, depending on policy and agreement specifics).
                </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits of Proper Funding</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Guaranteed Buyer & Seller:</strong> Creates a predetermined market and price for the departing owner's shares, avoiding disputes or forced sales at unfavorable terms.</li>
                <li><strong>Smooth Transition:</strong> Facilitates an orderly transfer of ownership, minimizing disruption to business operations.</li>
                <li><strong>Liquidity:</strong> Provides immediate cash needed for the buyout, preventing the need for the business or remaining owners to liquidate assets or take on excessive debt.</li>
                <li><strong>Fair Value for Heirs:</strong> Ensures the family/estate of a deceased owner receives a fair, agreed-upon value for their business interest.</li>
                <li><strong>Business Stability:</strong> Maintains continuity and reassures employees, customers, and lenders during ownership changes.</li>
                <li><strong>Cost-Effective (Insurance):</strong> Life insurance often provides the necessary funds at a fraction of the potential buyout cost, especially when established early. Death benefits are generally tax-free.</li>
              </ul>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Considerations</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Key factors to keep in mind:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Agreement Structure:</strong> Cross-Purchase vs. Entity-Purchase has different tax implications and administrative complexities (especially with many owners).</li>
                <li><strong>Valuation:</strong> The agreement must clearly define how the business will be valued. Regular reviews are essential to ensure the valuation and funding remain adequate.</li>
                <li><strong>Funding Mechanism Choice:</strong> While life insurance is common, other methods (cash reserves, installment payments, loans) exist but may lack certainty or immediate availability. Disability buyout insurance is also critical.</li>
                <li><strong>Premium Payments:</strong> Ensuring premiums are consistently paid is vital.</li>
                <li><strong>Policy Ownership & Beneficiary:</strong> Must align correctly with the agreement structure to avoid tax issues or complications.</li>
                <li><strong>Regular Reviews:</strong> The agreement, valuation, and funding levels should be reviewed periodically (e.g., annually or after major business events) and updated as needed.</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is This Strategy Essential for Your Business?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">A funded Buy-Sell Agreement is critical for nearly any privately-held business with multiple owners, including:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Partnerships</li>
                <li>Family Businesses</li>
                <li>Closely-Held Corporations (like S-corps or C-corps)</li>
                <li>Professional Practices (doctors, lawyers, accountants, etc.)</li>
                <li>Any business where the departure of an owner could create financial strain or operational chaos without a clear, funded plan.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Plan for Seamless Ownership Transitions</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                A well-funded Buy-Sell Agreement is the cornerstone of business continuity planning for multi-owner enterprises. Let's ensure your agreement is robust and the funding is secure.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Review Your Buy-Sell Strategy
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
         <BookingWidget onClose={() => setShowBookingWidget(false)} />
      )}
    </MarketingLayout>
  );
}